import React, {
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import {
  CssBaseline,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import clsx from 'clsx';
import api from '@shared/api/api';
import { Settings } from '@features/settings/pages/SettingsPage';
import dayjs from 'dayjs';
import useSound from 'use-sound';
import alertSoundShort from '@assets/alert_short.mp3';
import BasicsContext from '@shared/context/BasicsContext';
import refreshFunctionContext from '@shared/context/refreshFunctionContext';
import { AlertSoundProvider } from '@shared/context/AlertSoundContext';
import { SnackbarProvider } from '@shared/context/SnackbarContext';
import { UserInfoModal } from '@shared/components/layout/UserInfoModal';
import TDBToolbar from '@shared/components/layout/Toolbar';
import { TDBTabs } from '@shared/components/layout/Tabs';
import AvailabilityBlock from '@shared/components/layout/AvailabilityBlock';
import { ErrorAPI } from '@shared/components/ui/ErrorAPI';
import { AlertUser } from '@shared/components/feedback/AlertUser';
import { AlertSOS } from '@shared/components/feedback/AlertSOS';
import Auth from '@shared/services/Auth';
import useStyles from '@shared/styles/App.styles.js';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { MobileBlockScreen } from '@shared/components/layout/MobileBlockScreen';

// ------------------------------------------------------------------------ //
// REACT QUERY HOOKS
// ------------------------------------------------------------------------ //
import { useInProgressActivity, useDirectHoursStatus, useStopActivity } from '@features/activities/hooks/useActivityQueries';
import { usePendingActivityStop, markActivityForStop } from '@features/activities/hooks/usePendingActivityStop';
import { AutoStopAt8PMModal } from '@features/activities/components/AutoStopAt8PMModal';
import { useUserMemo } from '@features/memo/hooks/useMemoQueries';
import { useActiveUsers, useUserInfoModal } from '@shared/hooks/queries/useUserQueries';

// ------------------------------------------------------------------------ //
// APP
// ------------------------------------------------------------------------ //
const App = () => {
  const isMobile = useIsMobile();
  const classes = useStyles();
  const [playAlert] = useSound(alertSoundShort, { volume: 0.25 });
  const [openDrawer, setOpenDrawer] = useState(false);
  const [errorAPI, setErrorAPI] = useState(false);
  const [tokenModified, setTokenModified] = useState(false);
  const [settings, setSettings] = useState(false);
  const [basics, setBasics] = useState();
  const [alertMemo, setAlertMemo] = useState(false);
  const [alertInquiry, setAlertInquiry] = useState(false);
  const [inChat, setInChat] = useState(false);
  const [infoSOS, setInfoSOS] = useState();
  const [withTest, setWithTest] = useState(false);
  const [activeHourSelected, setActiveHourSelected] = useState(false);

  // ------------------------------------------------------------------------ //
  // REACT QUERY HOOKS
  // ------------------------------------------------------------------------ //
  const { data: memo = [] } = useUserMemo();
  const { refetch: refetchActiveUsers } = useActiveUsers();
  const { userInfo, hideUserInfo } = useUserInfoModal();
  const { data: inProgressActivity } = useInProgressActivity();
  const { data: directHoursStatus } = useDirectHoursStatus();
  const stopActivityMutation = useStopActivity();
  const [openAutoStopModal, setOpenAutoStopModal] = useState(false);
  const hasShownAutoStopModal = useRef(false);

  // Stop pending activity from previous session (if any)
  usePendingActivityStop();


  // ------------------------------------------------------------------------ //
  //  AUTO-STOP MODAL (hors horaires d'ouverture)
  // ------------------------------------------------------------------------ //
  useEffect(() => {
    if (directHoursStatus?.isWithinDirectHours === false && inProgressActivity && !hasShownAutoStopModal.current) {
      setOpenAutoStopModal(true);
      hasShownAutoStopModal.current = true;
    }
    if (directHoursStatus?.isWithinDirectHours === true) {
      hasShownAutoStopModal.current = false;
    }
  }, [directHoursStatus?.isWithinDirectHours, inProgressActivity]);

  const handleAutoStopActivity = async () => {
    setOpenAutoStopModal(false);
    if (inProgressActivity?.id) {
      stopActivityMutation.mutate(inProgressActivity.id);
    }
  };

  // ------------------------------------------------------------------------ //
  //  Refresh token
  // ------------------------------------------------------------------------ //
  const refreshToken = useCallback(async () => {
    try {
      const response = await api.getNewToken();
      if (response) {
        localStorage.setItem(import.meta.env.VITE_STORAGE_KEY_ACCESS_TOKEN, response.access_token);
        localStorage.setItem(import.meta.env.VITE_STORAGE_KEY_REFRESH_TOKEN, response.refresh_token);
      } else {
        setErrorAPI(true);
      }
    } catch (error) {
      console.log(error.response);
      if (
        error.response !== undefined &&
        error.response.status === 401 &&
        error.response.data.message === 'The refresh token is invalid.'
      ) {
        localStorage.removeItem(import.meta.env.VITE_STORAGE_KEY_ACCESS_TOKEN);
        localStorage.removeItem(import.meta.env.VITE_STORAGE_KEY_REFRESH_TOKEN);
        setTokenModified((tokenModified) => !tokenModified);
      } else {
        setErrorAPI(true);
      }
    }
  }, []);

  // ------------------------------------------------------------------------ //
  //  SET basics AND SET isLogin
  // ------------------------------------------------------------------------ //
  useEffect(() => {
    // Recharger les basics depuis l'API au chargement de la page
    const loadBasics = async () => {
      try {
        const freshBasics = await api.getBasics();
        localStorage.setItem('basics', JSON.stringify(freshBasics));
        setBasics(freshBasics);
      } catch (error) {
        // En cas d'erreur, utiliser les basics du localStorage
        console.error('Erreur lors du chargement des basics:', error);
        setBasics(Auth.basics());
      }
    };

    if (Auth.basics()) {
      loadBasics();
    }
  }, [tokenModified, refreshToken]);

  // ------------------------------------------------------------------------ //
  //  PLAY SOUND WHEN IS MEMO TIME
  // ------------------------------------------------------------------------ //
  useEffect(() => {
    const notified = [];

    const isMemoTime = (memoItem) => {
      const diff = dayjs(memoItem.alarm_at).unix() - dayjs().unix();
      const hasBeenNotified = notified.find((m) => m.id === memoItem.id);

      if (diff <= 60 && diff >= 0 && !hasBeenNotified) {
        setAlertMemo(memoItem);
        console.log('MEMO ALERT....');
        playAlert();
        notified.push(memoItem);
      }
    };

    const interval = setInterval(() => memo.forEach(isMemoTime), 20000);

    return () => clearInterval(interval);
  }, [memo, playAlert]);

  // ------------------------------------------------------------------------ //
  //  PREVENT CLOSE BROWSER WHEN ACTIVITY IN PROGRESS & MARK FOR STOP ON EXIT
  // ------------------------------------------------------------------------ //
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (inProgressActivity) {
        // Show browser confirmation dialog
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    const handlePageHide = () => {
      // Mark activity for stopping when page reopens
      if (inProgressActivity && basics?.user?.id) {
        markActivityForStop(inProgressActivity.id, basics.user.id);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [inProgressActivity, basics]);

  // ------------------------------------------------------------------------ //
  //  HANDLERS
  // ------------------------------------------------------------------------ //
  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleWithTest = (event) => {
    setWithTest(event.target.checked);
  };

  const handleCloseAlertSOS = async (event) => {
    setInfoSOS(null);
    try {
      const response = await api.putUserInfo({ userId: basics.user.id, sosFrom: 0 });
      if (response.status !== 200) {
        setErrorAPI(true);
      }
    } catch (error) {
      console.log(error);
      if (
        error.response !== undefined &&
        error.response.status === 401 &&
        error.response.data.message === 'Unauthenticated.'
      ) {
        await refreshToken();
        handleCloseAlertSOS();
      } else {
        setErrorAPI(true);
      }
    }
  };

  const handleCloseAlertMemo = async (event) => {
    setAlertMemo(false);
  };

  const handleCloseAlertInquiry = async (event) => {
    setAlertInquiry(false);
  };

  // ------------------------------------------------------------------------ //
  //  RENDERING
  // ------------------------------------------------------------------------ //
  if (isMobile) return <MobileBlockScreen />;

  return (
    <AlertSoundProvider>
      <SnackbarProvider>
      <BasicsContext.Provider value={basics}>
        <refreshFunctionContext.Provider value={refreshToken}>
          <div className={classes.root}>
          <CssBaseline />

          <TDBToolbar
            openDrawer={openDrawer}
            handleCloseDrawer={handleCloseDrawer}
            handleOpenDrawer={handleOpenDrawer}
            setSettings={setSettings}
            tokenModified={tokenModified}
            setTokenModified={setTokenModified}
            activeHourSelected={activeHourSelected}
            setActiveHourSelected={setActiveHourSelected}
          />

          <main
            className={clsx(classes.content, {
              [classes.contentShift]: openDrawer,
            })}
          >
            <div className={classes.drawerHeader} />

            <ErrorAPI
              errorAPI={errorAPI}
              setErrorAPI={setErrorAPI}
              message={
                'Un problème est survenu dans le tableau de bord Prof Express, veuillez recharger la page ou réessayer plus tard.'
              }
            />

            {settings ? ( // OPEN SETTING PAGE
              <Settings setSettings={setSettings} />
            ) : ( // DISPLAY DASHBOARD
              <>
                {alertMemo && (
                  <AlertUser
                    alertUser={true}
                    setAlertUser={handleCloseAlertMemo}
                    title={'Alert memo : ' + alertMemo.description}
                    type={'info'}
                    position={'right'}
                  />
                )}

                {infoSOS && (
                  <AlertSOS
                    alertSOS={infoSOS}
                    handleCloseAlertSOS={handleCloseAlertSOS}
                    title={infoSOS.first_name + ' ' + infoSOS.last_name + ' souhaite vous parler'}
                    message={'Connecter vous sur Skype pour voir ses messages'}
                    type={'warning'}
                    position={'right'}
                  />
                )}

                {alertInquiry && (
                  <AlertUser
                    alertUser={alertInquiry}
                    setAlertUser={handleCloseAlertInquiry}
                    title={'Nouvelle Requête !'}
                    type={'warning'}
                    position={'center'}
                  />
                )}

                <div className={classes.fixedRow}>
                  <AvailabilityBlock
                    inChat={inChat}
                    setInChat={setInChat}
                    startUnavailability={null}
                    endUnavailability={null}
                  />
                </div>

                {basics && basics.user.test_inquiries === 1 && (
                  <>
                    <FormControlLabel
                      control={<Checkbox checked={withTest} onChange={handleWithTest} name="test" color="primary" />}
                      label="Afficher les requêtes test"
                    />
                  </>
                )}
                <TDBTabs
                  setSettings={setSettings}
                  activeHourSelected={activeHourSelected}
                />
              </>
            )}

            <AutoStopAt8PMModal
              openModal={openAutoStopModal}
              handleCloseModal={() => setOpenAutoStopModal(false)}
              handleStopActivity={handleAutoStopActivity}
              activityId={inProgressActivity?.id}
            />

            <UserInfoModal
              userInfo={userInfo}
              handleCloseModal={() => hideUserInfo()}
            />
          </main>
        </div>
        </refreshFunctionContext.Provider>
      </BasicsContext.Provider>
      </SnackbarProvider>
    </AlertSoundProvider>
  );
};

export default App;
